import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import type { ChangeEvent, ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'

import OfferEditorFeaturesGroupModal from '../../modals/OfferEditorFeaturesGroupModal'
import OfferEditorInAppsModal from '../../modals/OfferEditorInAppsModal'

import AppLogo from '~/components/AppLogo'
import BackNavigation from '~/components/BackNavigation'
import Badge from '~/components/Badge'
import Button from '~/components/Buttons/Button'
import ContentLayout from '~/components/ContentLayout'
import IapCard from '~/components/IapCard'
import InAppResume from '~/components/InAppResume'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import Section from '~/components/Section'
import Tooltip from '~/components/Tooltip'
import ViewTitle from '~/components/ViewTitle'
import { DEVICE_TYPE, FORM_ERRORS } from '~/const'
import { FASTLY_PATH } from '~/const/asset'
import { CATEGORY_URLS } from '~/const/category-urls'
import { MAX_BADGES_LIMIT } from '~/const/iap'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppsManagementListQuery } from '~/queries/application-management'
import {
  iapKeys,
  useCreateOfferMutation,
  useDeleteOfferMutation,
  useOfferDetailsQuery,
  useUpdateOfferMutation,
} from '~/queries/iap'
import { type IInAppFeaturesGroupWithAppIcon, type IInAppWithAppIconUrl } from '~/types/iap'
import { resize } from '~/utils/media'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface FormValues {
  available_in_dev: boolean
  available_in_prod: boolean
  feature_group_ids: IInAppFeaturesGroupWithAppIcon[]
  in_apps: IInAppWithAppIconUrl[]
  name: string
}

const inAppsSchema = Yup.object({
  app_icon_url: Yup.string().nullable(),
  app_key: Yup.string().required(),
  created_at: Yup.number().required(),
  default_price_usd: Yup.number(),
  device_type: Yup.mixed<DeviceType>().oneOf(Object.values(DEVICE_TYPE)).required(),
  free_trial: Yup.string(),
  intro_offer_duration_in_days: Yup.number(),
  intro_offer_price_usd: Yup.number(),
  original_status: Yup.string(),
  periodicity: Yup.string(),
  sku: Yup.string().required(),
  status: Yup.string(),
  store_id: Yup.string(),
  sub_type: Yup.string(),
  type: Yup.string().required(),
})

const featuresSchema = Yup.object({
  app_icon_url: Yup.string().nullable(),
  app_key: Yup.string().required(),
  device_type: Yup.mixed<DeviceType>().oneOf(Object.values(DEVICE_TYPE)).required(),
  feature_group_id: Yup.string().required(),
  feature_ids: Yup.array().of(Yup.string().required()).nullable(),
  is_default: Yup.boolean().required(),
  name: Yup.string().required(),
})

const validationSchema = Yup.object()
  .shape({
    available_in_dev: Yup.boolean().required(),
    available_in_prod: Yup.boolean().required(),
    feature_group_ids: Yup.array().of(featuresSchema).nullable().default([]).required(),
    in_apps: Yup.array().of(inAppsSchema).nullable().default([]).required(),
    name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  })
  .required()

const OffersEditorView = (): ReactElement => {
  /** Local state */

  const { data: apps } = useAppsManagementListQuery()

  const navigate = useNavigate()

  const { offerId } = useParams()

  const queryClient = useQueryClient()

  const [isInAppsModalOpen, setIsInAppsModalOpen] = useState(false)

  const [isFeaturesGroupsModalOpen, setIsFeaturesGroupsModalOpen] = useState(false)

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false)

  /** Queries & Mutations */

  const { data: offerDetails, isLoading } = useOfferDetailsQuery({ offerId })

  const { mutateAsync: createOffer } = useCreateOfferMutation()

  const { mutateAsync: updateOffer } = useUpdateOfferMutation()

  const { mutateAsync: deleteOffer } = useDeleteOfferMutation()

  /** Query dependent state */

  const defaultValues: FormValues = useMemo(
    () => ({
      available_in_dev: false,
      available_in_prod: false,
      feature_group_ids: [],
      in_apps: [],
      name: '',
    }),
    []
  )

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    //@ts-expect-error schema typing
    resolver: yupResolver<FormValues>(validationSchema),
  })

  const inAppsWithIconUrl = useMemo(
    () =>
      offerDetails?.in_apps?.map((el) => {
        const iconFileId = apps?.find(({ app_key }) => app_key === el.app_key)?.icon_file_id
        return {
          ...el,
          app_icon_url: iconFileId ? resize(`${FASTLY_PATH}${iconFileId}`, 32, 32) : undefined,
        }
      }),
    [apps, offerDetails?.in_apps]
  )

  const featuresGroupsWithIconUrl = useMemo(
    () =>
      offerDetails?.features_groups?.map((el) => {
        const iconFileId = apps?.find(({ app_key }) => app_key === el.app_key)?.icon_file_id
        return {
          ...el,
          app_icon_url: iconFileId ? resize(`${FASTLY_PATH}${iconFileId}`, 32, 32) : undefined,
        }
      }),
    [apps, offerDetails?.features_groups]
  )

  const inAppsValues = watch('in_apps')

  const featuresGroupsValues = watch('feature_group_ids')

  /** Handlers */

  const handleInAppsModalState = (isOpen: boolean) => () => setIsInAppsModalOpen(isOpen)

  const handleFeaturesGroupsModalState = (isOpen: boolean) => () => setIsFeaturesGroupsModalOpen(isOpen)

  const handleCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>,
    field: 'available_in_dev' | 'available_in_prod'
  ) => {
    setValue(field, event.target.checked, { shouldDirty: true, shouldTouch: true })
  }

  const handleSetInAppsValue = (inApps: IInAppWithAppIconUrl[]) => {
    const valuesWithoutDoubles = inApps.filter(({ sku }) => !inAppsValues.some((inApp) => inApp.sku === sku))

    setValue('in_apps', [...inAppsValues, ...valuesWithoutDoubles], { shouldDirty: true, shouldTouch: true })
    setIsInAppsModalOpen(false)
  }

  const handleSetFeaturesGroupsValue = (featuresGroups: IInAppFeaturesGroupWithAppIcon[]) => {
    const valuesWithoutDoubles = featuresGroups.filter(
      ({ feature_group_id }) =>
        !featuresGroupsValues.some((featureGroup) => featureGroup.feature_group_id === feature_group_id)
    )

    setValue('feature_group_ids', [...featuresGroupsValues, ...valuesWithoutDoubles], {
      shouldDirty: true,
      shouldTouch: true,
    })
    setIsFeaturesGroupsModalOpen(false)
  }

  const handleDeleteInAppsClick = (sku: string) =>
    setValue(
      'in_apps',
      inAppsValues.filter((inApp) => inApp.sku !== sku),
      { shouldDirty: true, shouldTouch: true }
    )

  const handleDeleteFeatureGroupClick = (featureGroupId: string) =>
    setValue(
      'feature_group_ids',
      featuresGroupsValues.filter((feature) => feature.feature_group_id !== featureGroupId),
      { shouldDirty: true, shouldTouch: true }
    )

  const handleFormSubmit = async (values: FormValues) => {
    try {
      offerId
        ? await updateOffer({
            ...values,
            feature_group_ids: values.feature_group_ids.map(({ feature_group_id }) => feature_group_id),
            offer_id: offerId,
          })
        : await createOffer({
            ...values,
            feature_group_ids: values.feature_group_ids.map(({ feature_group_id }) => feature_group_id),
          })
      await queryClient.invalidateQueries({ queryKey: iapKeys.offersList() })
      reset(values)
      displayToast(`Offer was successfully ${offerId ? 'updated' : 'created'}`, 'success')
      !offerId && navigate(`${CATEGORY_URLS.IN_APP_PURCHASES}/offers`)
    } catch (e) {
      captureException(e)
      displayToast(`Offer could not be ${offerId ? 'updated' : 'created'}`, 'error')
    }
  }

  const handleDeleteModalState = (isOpen: boolean) => () => setIsConfirmDeleteModalOpen(isOpen)

  const handleDeleteConfirm = async () => {
    if (offerId) {
      try {
        await deleteOffer(offerId)
        await queryClient.invalidateQueries({ queryKey: [...iapKeys.all, 'offers-list'] })
        displayToast('Offer was successfully deleted', 'success')
        setIsConfirmDeleteModalOpen(false)
        navigate(`${CATEGORY_URLS.IN_APP_PURCHASES}/offers`)
      } catch (e) {
        captureException(e)
        displayToast('Offer could not be deleted', 'error')
      }
    }
  }

  /** Effects */

  useDocumentTitle('IAP - Offers Editor')

  useEffect(() => {
    reset({
      available_in_dev: offerDetails?.available_in_dev || false,
      available_in_prod: offerDetails?.available_in_prod || false,
      feature_group_ids: featuresGroupsWithIconUrl ?? [],
      in_apps: inAppsWithIconUrl ?? [],
      name: offerDetails?.name || '',
    })
  }, [featuresGroupsWithIconUrl, inAppsWithIconUrl, offerDetails, reset])

  /** Render */

  return (
    <>
      <Layout className="in-apps-offers-editor-view" withPadding>
        <ViewTitle>
          <BackNavigation to={`${CATEGORY_URLS.IN_APP_PURCHASES}/offers`} /> IN-APPS OFFERS - Editor
        </ViewTitle>
        <ContentLayout loading={isLoading}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="in-apps-offers-editor-view__header">
              <div className="in-apps-offers-editor-view__header__left-section">
                <TextInput
                  className="in-apps-offers-editor-view__header__left-section__search-input"
                  defaultValue={defaultValues.name}
                  error={errors.name?.message}
                  placeholder="Enter offer name"
                  {...register('name')}
                />
                <CheckboxInput
                  {...register('available_in_dev')}
                  checked={watch('available_in_dev')}
                  label="Dev"
                  labelPosition="right"
                  onChange={(event) => handleCheckboxChange(event, 'available_in_dev')}
                />
                <CheckboxInput
                  {...register('available_in_prod')}
                  checked={watch('available_in_prod')}
                  label="Prod"
                  labelPosition="right"
                  onChange={(event) => handleCheckboxChange(event, 'available_in_prod')}
                />
              </div>
              <div className="in-apps-offers-editor-view__header__right-section">
                {offerId && isDirty ? (
                  <span className="in-apps-offers-editor-view__header__right-section__save-message">
                    Don&apos;t forget to save your changes!
                  </span>
                ) : null}
                <Button disabled={!isDirty || isSubmitting} type="submit">
                  {offerDetails ? 'Save' : 'Create'}
                </Button>
                {offerId ? (
                  <Button
                    className="in-apps-offers-editor-view__header__right-section__delete-button"
                    onClick={handleDeleteModalState(true)}
                  >
                    Delete
                  </Button>
                ) : null}
              </div>
            </div>
            <Section className="in-apps-offers-editor-view__content">
              <>
                <div className="in-apps-offers-editor-view__content__in-apps-container content-container">
                  <Button
                    className="content-container__action-button"
                    onClick={handleInAppsModalState(true)}
                    size="small"
                  >
                    Add In-App
                  </Button>
                  {inAppsValues.map((inApp, index) => (
                    <IapCard
                      content={
                        <div className="inapp-card-component__left__body__logo-in-app-resume-container">
                          <AppLogo appLogoUrl={inApp.app_icon_url} appPlatform={inApp.device_type} size="small" />
                          <InAppResume inApp={inApp} />
                        </div>
                      }
                      key={`${inApp.app_key}-${inApp.device_type}-${index}`}
                      onRemoveClick={() => handleDeleteInAppsClick(inApp.sku)}
                      type="remove"
                    />
                  ))}
                </div>
                <div className="in-apps-offers-editor-view__content__unlock-arrow content-container">
                  <span>Unlock</span>
                  <svg fill="none" height="16" viewBox="0 0 98 16" width="98" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M97.2071 8.70711C97.5976 8.31658 97.5976 7.68342 97.2071 7.29289L90.8431 0.928932C90.4526 0.538408 89.8195 0.538408 89.4289 0.928932C89.0384 1.31946 89.0384 1.95262 89.4289 2.34315L95.0858 8L89.4289 13.6569C89.0384 14.0474 89.0384 14.6805 89.4289 15.0711C89.8195 15.4616 90.4526 15.4616 90.8431 15.0711L97.2071 8.70711ZM0.5 9H96.5V7H0.5V9Z"
                      fill="black"
                      fillOpacity="0.5"
                    />
                  </svg>
                </div>
                <div className="in-apps-offers-editor-view__content__features-group-container content-container">
                  <Button
                    className="content-container__action-button"
                    onClick={handleFeaturesGroupsModalState(true)}
                    size="small"
                  >
                    Add Features Group
                  </Button>
                  {featuresGroupsValues.map(
                    ({ app_icon_url, app_key, device_type, feature_group_id, feature_ids, name }) => (
                      <IapCard
                        content={
                          feature_ids ? (
                            <div className="iap-card-badge-container">
                              {feature_ids.slice(0, MAX_BADGES_LIMIT).map((id) => (
                                <Badge className="in-app-badge" key={id} outlined value={id} />
                              ))}
                              {feature_ids?.length > MAX_BADGES_LIMIT ? (
                                <Tooltip
                                  align={{ points: ['br', 'tr'] }}
                                  overlay={
                                    <div className="iap-card-badge-tooltip-content">
                                      {feature_ids
                                        ?.slice(MAX_BADGES_LIMIT)
                                        .map((id) => <Badge className="in-app-badge" key={id} outlined value={id} />)}
                                    </div>
                                  }
                                  placement="top"
                                  trigger="hover"
                                >
                                  <div className="iap-card-badge-container__items-counter">
                                    +{feature_ids?.length - MAX_BADGES_LIMIT}
                                  </div>
                                </Tooltip>
                              ) : null}
                            </div>
                          ) : (
                            <div className="no-feature-wrapper">
                              <span className="no-feature-wrapper__message">No Features in this group</span>
                            </div>
                          )
                        }
                        key={`${app_key}-${device_type}-${feature_group_id}`}
                        onRemoveClick={() => handleDeleteFeatureGroupClick(feature_group_id)}
                        titleComponent={
                          <div className="inapp-card-component__left__header__title__features-group-logo">
                            <AppLogo appLogoUrl={app_icon_url} appPlatform={device_type} size="small" />
                            <span>{name}</span>
                          </div>
                        }
                        type="remove"
                      />
                    )
                  )}
                </div>
              </>
            </Section>
          </form>
        </ContentLayout>
      </Layout>
      <OfferEditorInAppsModal
        isOpen={isInAppsModalOpen}
        onAddClick={handleSetInAppsValue}
        onClose={handleInAppsModalState(false)}
        selectedInApps={inAppsValues}
      />
      <OfferEditorFeaturesGroupModal
        isOpen={isFeaturesGroupsModalOpen}
        onAddClick={handleSetFeaturesGroupsValue}
        onClose={handleFeaturesGroupsModalState(false)}
      />
      <ModalDialog
        isOpen={isConfirmDeleteModalOpen}
        mainText="Are you sure you want to delete this offer?"
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}

export default OffersEditorView
